import { Flex } from "antd";
import { get } from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import Preloader from "~/components/Preloader";
import { useAppeals } from "~/entity/Appeals";
import {
  IAppealFile,
  IAppealSchema,
  RESOLUTION_STATUS,
} from "~/entity/Appeals/model/schema";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { CommentWrapper } from "~/shared/ui/CommentWrapper";
import Modal from "~/shared/ui/Fields/Modal";
import RedesignButton from "~/shared/ui/Fields/RedesignButton";
import s from "./AppealView.less";

interface IAppealViewProps {
  onClose?: () => void;
}

interface IDataViewItem {
  title: string;
  comment?: string;
  files?: IAppealFile[];
  style?: React.CSSProperties;
}

interface IDataViewBlockProps {
  item: IDataViewItem;
  getFileNameWithoutExtension: (name: string) => string;
  getMimeTypeShort: (mimeType: string) => string;
}

export const AppealView = ({ onClose: onCloseProp }: IAppealViewProps) => {
  const { t } = useTranslation();
  const setData = useSetLocationData();
  const { openModal, currentId } = useGetLocationData();
  const { queryKeys, getAppealById } = useAppeals();

  const isOpenModal = openModal === "appealFiles";
  const appealQuery = useQuery({
    queryKey: queryKeys.byId(currentId),
    queryFn: () => getAppealById(currentId),
    enabled: !!isOpenModal && !!currentId,
  });
  const appealInstance = get(
    appealQuery.data,
    "data.appeal",
    undefined
  ) as IAppealSchema;
  const getFileNameWithoutExtension = (name: string) =>
    name.replace(/\.[^.]+$/, "");

  const getMimeTypeShort = (mimeType: string) => {
    const parts = mimeType.split("/");
    return parts[parts.length - 1].toUpperCase();
  };

  const isOpen = isOpenModal ?? openModal === "appealFiles";
  const handleClose = onCloseProp ?? (() => setData("openModal", ""));

  const showDataToView = () => {
    const dataToViewBlock1: IDataViewItem[] = [];
    const dataToViewBlock2: IDataViewItem[] = [];
    const dataToViewBlock3: IDataViewItem[] = [];
    if (appealInstance?.contactPersonFio) {
      dataToViewBlock1.push({
        title: t("appeal.responsiblePerson"),
        comment: appealInstance?.contactPersonFio,
      });
    }
    if (appealInstance?.comment) {
      dataToViewBlock1.push({
        title: t("appeal.responsiblePersonComment"),
        comment: appealInstance?.comment,
      });
    }
    if (appealInstance?.files.length) {
      const appealFiles = appealInstance?.files.filter(
        (file) => file.scope === "appeal"
      );
      !!appealFiles.length &&
        dataToViewBlock1.push({
          title: t("appeal.responsiblePersonFiles"),
          files: appealFiles,
        });
    }
    if (appealInstance?.needInfoComment) {
      dataToViewBlock2.push({
        title: t("appeal.checkerComment"),
        comment:
          typeof appealInstance.needInfoComment === "string"
            ? appealInstance.needInfoComment
            : undefined,
      });
    }
    if (
      appealInstance?.resolutionStatusFrontend === RESOLUTION_STATUS.accepted ||
      appealInstance?.resolutionStatusFrontend === RESOLUTION_STATUS.rejected
    ) {
      dataToViewBlock3.push({
        title: `${t("appeal.resolution")}`,
        style: {
          padding: 3,
          backgroundColor:
            appealInstance?.resolutionStatusFrontend ===
            RESOLUTION_STATUS.accepted
              ? "#e6f7e6"
              : "#ffe6e6",
          borderRadius: 4,
        },
        comment: appealInstance?.resolutionComment,
      });
    }
    if (appealInstance?.files.length) {
      const resolutionFiles = appealInstance?.files.filter(
        (file) => file.scope !== "appeal"
      );
      !!resolutionFiles.length &&
        dataToViewBlock3.push({
          title: t("appeal.checkerFiles"),
          files: resolutionFiles,
        });
    }

    const allData = [...dataToViewBlock1, ...dataToViewBlock2];
    return allData.length ? (
      <Flex style={{ width: "100%" }} gap={12} vertical>
        {dataToViewBlock1.map((item, index) => (
          <React.Fragment key={item.title}>
            <DataViewBlock
              item={item}
              getFileNameWithoutExtension={getFileNameWithoutExtension}
              getMimeTypeShort={getMimeTypeShort}
            />
          </React.Fragment>
        ))}
        {dataToViewBlock2.map((item, index) => (
          <React.Fragment key={item.title}>
            {index === 0 && !!dataToViewBlock1.length && (
              <span
                style={{ width: "100%", height: "1px", background: "#adb7bd" }}
              />
            )}
            <DataViewBlock
              item={item}
              getFileNameWithoutExtension={getFileNameWithoutExtension}
              getMimeTypeShort={getMimeTypeShort}
            />
          </React.Fragment>
        ))}
        {dataToViewBlock3.map((item, index) => (
          <React.Fragment key={item.title}>
            {index === 0 && !!dataToViewBlock2.length && (
              <span
                style={{ width: "100%", height: "1px", background: "#adb7bd" }}
              />
            )}
            <DataViewBlock
              item={item}
              getFileNameWithoutExtension={getFileNameWithoutExtension}
              getMimeTypeShort={getMimeTypeShort}
            />
          </React.Fragment>
        ))}
      </Flex>
    ) : (
      <></>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      title={t("appeal.appealInfo")}
      actionClose={handleClose}
      customClassName={s.appealFilesModal}
    >
      <Flex vertical gap={12} align="start" style={{ width: "100%" }}>
        {appealQuery.isLoading ? <Preloader /> : showDataToView()}
        <div className={s.appealFilesActions}>
          <RedesignButton
            theme="main"
            txtBtn={t("appeal.close")}
            onClick={handleClose}
          />
        </div>
      </Flex>
    </Modal>
  );
};

const DataViewBlock = ({
  item,
  getFileNameWithoutExtension,
  getMimeTypeShort,
}: IDataViewBlockProps) => {
  return (
    <>
      <CommentWrapper
        title={item.title}
        comment={item.comment}
        style={item.style}
      />
      <Flex
        vertical
        gap={8}
        align="start"
        className={s.appealFilesList}
        style={{ width: "100%" }}
      >
        {item?.files?.length &&
          item?.files?.map((file) => (
            <div key={file.id} className={s.appealFilesItem}>
              <a
                href={file.link}
                target={"_blank"}
                className={s.appealFilesName}
              >
                {getFileNameWithoutExtension(file.name)}
              </a>
              <span className={s.appealFilesMime}>
                ({getMimeTypeShort(file.mimeType)})
              </span>
            </div>
          ))}
      </Flex>
    </>
  );
};
